import { slugify } from '../format/format';

import {
  AVAILABILITY_KEYS,
  CURRENTLY_UNAVAILABLE_KEYS,
  DEAL_AND_REBATE_KEYS,
  FILTER_CATEGORY_CODES,
  FILTER_KEYS
} from '../../constants/filters';
import { PRODUCT_TYPES } from '../../constants/products';
import { SORT_OPTIONS } from '../../constants/search-types';

const FILTER_CATEGORY_NAMES = {
  AVAILABILITY: 'Get it Fast',
  BUNDLE_PRICE: 'Package Price',
  CURRENTLY_UNAVAILABLE: 'Currently Unavailable',
  DEALS_AND_REBATES: 'Deals and Rebates',
  FLEET_PREFERRED_PRODUCTS: 'Fleet Preferred Products',
  PRICE: 'Price Range',
  QUICK_FILTERS: 'Quick Filters'
};

function isAvailabilityFilter(categoryCode) {
  return (
    typeof categoryCode === 'string' &&
    Object.values(AVAILABILITY_KEYS).some(code => code === categoryCode)
  );
}

function isCurrentlyUnavailableFilter(categoryCode) {
  return (
    typeof categoryCode === 'string' &&
    Object.values(CURRENTLY_UNAVAILABLE_KEYS).some(code =>
      code.includes(categoryCode)
    )
  );
}

function isFleetPreferredProductFilter(categoryCode) {
  return (
    typeof categoryCode === 'string' &&
    categoryCode === FILTER_CATEGORY_CODES.FLEET_PREFERRED_PRODUCTS
  );
}
function isQuickFilter(categoryCode) {
  return (
    typeof categoryCode === 'string' &&
    /boolean|inventoryStatus|storeInStock|vendorStockAvailabilityDays/.test(
      categoryCode
    )
  );
}

function isDealOrRebate(categoryCode) {
  return (
    typeof categoryCode === 'string' &&
    Object.values(DEAL_AND_REBATE_KEYS).some(code => code === categoryCode)
  );
}

function createFilterCategory(categoryCode, categoryName, categoryFilters) {
  return {
    categoryCode,
    categoryName,
    description: categoryFilters?.[0]?.description,
    isOpenByDefault: true,
    showMoreByDefault: true,
    values: categoryFilters.reduce((acc, filter) => {
      const value = filter?.values?.[0];
      if (value) {
        acc.push({
          ...value,
          categoryName,
          valueName: filter.categoryName
        });
      }

      return acc;
    }, [])
  };
}

function getEnhancedFilters(filters, options) {
  const availabilityFilters = [];
  const currentlyUnavailableFilters = [];
  const fleetPreferredProductFilters = [];
  const quickFilters = [];
  const dealsAndRebatesFilters = [];
  const otherFilters = [];

  for (let i = 0; i < filters.length; i++) {
    const filter = filters[i];
    const categoryCode = filter?.categoryCode;

    if (options?.hasGetItFast && isAvailabilityFilter(categoryCode)) {
      availabilityFilters.push(filter);
    } else if (isCurrentlyUnavailableFilter(categoryCode)) {
      currentlyUnavailableFilters.push(filter);
    } else if (isDealOrRebate(categoryCode)) {
      dealsAndRebatesFilters.push(filter);
    } else if (isFleetPreferredProductFilter(categoryCode)) {
      fleetPreferredProductFilters.push(filter);
    } else if (isQuickFilter(categoryCode)) {
      quickFilters.push(filter);
    } else {
      otherFilters.push(filter);
    }
  }

  const availabilityFiltersCategory = createFilterCategory(
    FILTER_CATEGORY_CODES.AVAILABILITY_FILTERS,
    FILTER_CATEGORY_NAMES.AVAILABILITY,
    availabilityFilters
  );

  const fleetPreferredCategory = createFilterCategory(
    FILTER_CATEGORY_CODES.FLEET_PREFERRED_PRODUCTS,
    FILTER_CATEGORY_NAMES.FLEET_PREFERRED_PRODUCTS,
    fleetPreferredProductFilters
  );

  const quickFiltersCategory = createFilterCategory(
    FILTER_CATEGORY_CODES.QUICK_FILTERS,
    FILTER_CATEGORY_NAMES.QUICK_FILTERS,
    quickFilters
  );

  const dealsAndRebatesFiltersCategory = createFilterCategory(
    FILTER_CATEGORY_CODES.DEALS_AND_REBATES_FILTERS,
    FILTER_CATEGORY_NAMES.DEALS_AND_REBATES,
    dealsAndRebatesFilters
  );

  const currentlyUnavailableFiltersCategory = createFilterCategory(
    FILTER_CATEGORY_CODES.CURRENTLY_UNAVAILABLE_FILTERS,
    FILTER_CATEGORY_NAMES.CURRENTLY_UNAVAILABLE,
    currentlyUnavailableFilters
  );

  let enhancedFilters = [...otherFilters];

  if (dealsAndRebatesFilters.length > 0) {
    const [firstFilter, ...allOtherFilters] = otherFilters;

    enhancedFilters = [
      firstFilter,
      dealsAndRebatesFiltersCategory,
      ...allOtherFilters
    ];
  }

  if (quickFilters.length > 0) {
    enhancedFilters.unshift(quickFiltersCategory);
  }

  if (options?.hasGetItFast) {
    enhancedFilters.unshift(availabilityFiltersCategory);
  }

  if (fleetPreferredProductFilters.length > 0) {
    enhancedFilters.unshift(fleetPreferredCategory);
  }

  if (currentlyUnavailableFilters.length > 0) {
    enhancedFilters.push(currentlyUnavailableFiltersCategory);
  }

  return enhancedFilters;
}

function facetsToFilters(facets, options = { hasGetItFast: false }) {
  if (!facets) {
    return [];
  }

  const sortedFacets = [...facets].sort(
    (f1, f2) => (f2.priority || 0) - (f1.priority || 0)
  );

  const filters = sortedFacets.reduce((acc, facet) => {
    if (
      !facet ||
      !facet.code ||
      facet.code.includes('tires') ||
      facet.code.includes('wheels')
    ) {
      return acc;
    }
    acc.push({
      categoryCode: facet.code,
      categoryName: facet.name,
      description: facet.description,
      isOpenByDefault: true,
      showMoreByDefault: facet.needShowMore,
      values: facet.values?.map(value => ({
        categoryCode: facet.code,
        categoryName: facet.name,
        count: value?.count,
        description: facet.description,
        isSelected: value?.selected,
        plpToolTip: value?.plpToolTip,
        scaleMaxVal: value?.scaleMaxVal,
        scaleMinVal: value?.scaleMinVal,
        sliderMaxVal: value?.sliderMaxVal,
        sliderMinVal: value?.sliderMinVal,
        valueCode: value?.code,
        valueName: value?.name
      }))
    });

    return acc;
  }, []);

  return getEnhancedFilters(filters, options);
}

function getMatchingSelectedFilterIndex(filter, filters) {
  return (filters || []).findIndex(
    f =>
      f.categoryCode === filter.categoryCode && f.valueCode === filter.valueCode
  );
}

function sortSelectedFilters(newFilters, oldFilters) {
  return newFilters.sort((f1, f2) => {
    const i1 = getMatchingSelectedFilterIndex(f1, oldFilters);
    const i2 = getMatchingSelectedFilterIndex(f2, oldFilters);

    if (i1 === -1) {
      return 1;
    } else if (i2 === -1) {
      return -1;
    } else {
      return i1 - i2;
    }
  });
}

function facetsToSelectedFilters(facets, oldFilters, query, options) {
  const selectedFilters = (Array.isArray(facets) ? facets : []).reduce(
    (selectedFiltersAcc, facet) => {
      if (!facet?.code) {
        return selectedFiltersAcc;
      }

      if (
        options?.hasGetItFast &&
        isAvailabilityFilter(facet.code) &&
        facet.values?.[0]?.selected
      ) {
        selectedFiltersAcc.push({
          categoryCode: facet.code,
          categoryName: FILTER_CATEGORY_NAMES.AVAILABILITY,
          valueCode: facet.values?.[0]?.code,
          valueName:
            facet.code === FILTER_KEYS.STORE_IN_STOCK
              ? 'Available now'
              : facet.name
        });
      } else if (isQuickFilter(facet.code) && facet.values?.[0]?.selected) {
        selectedFiltersAcc.push({
          categoryCode: facet.code,
          categoryName: FILTER_CATEGORY_NAMES.QUICK_FILTERS,
          valueCode: facet.values?.[0]?.code,
          valueName: facet.name
        });
      } else if (
        isCurrentlyUnavailableFilter(facet.code) &&
        facet.values?.[0]?.selected
      ) {
        selectedFiltersAcc.push({
          categoryCode: facet.code,
          categoryName: FILTER_CATEGORY_NAMES.CURRENTLY_UNAVAILABLE,
          valueCode: facet.values?.[0]?.code,
          valueName: facet.name
        });
      } else if (Array.isArray(facet.values)) {
        const valuesArr = facet.values.reduce((valuesAcc, value) => {
          if (!value?.selected) {
            return valuesAcc;
          }

          valuesAcc.push({
            categoryCode: facet.code,
            categoryName: facet.name,
            valueCode: value.code,
            valueName: value.name
          });

          return valuesAcc;
        }, []);

        selectedFiltersAcc = selectedFiltersAcc.concat(valuesArr);
      }

      return selectedFiltersAcc;
    },
    []
  );

  const priceRangeFilter = getPriceRangeFilter(query);
  if (priceRangeFilter) {
    selectedFilters.push(priceRangeFilter);
  }

  const bundlePriceRangeFilter = getPriceRangeFilter(query, true);
  if (bundlePriceRangeFilter) {
    selectedFilters.push(bundlePriceRangeFilter);
  }

  return sortSelectedFilters(selectedFilters, oldFilters);
}

function getPriceRangeFilter(query, isBundle) {
  const priceRangeMatch = (
    isBundle
      ? /bundlePriceValue:\[(\d+) TO (\d+)\]/
      : /priceValue:\[(\d+) TO (\d+)\]/
  ).exec(query);
  if (
    Array.isArray(priceRangeMatch) &&
    priceRangeMatch[1] &&
    priceRangeMatch[2]
  ) {
    return {
      categoryCode: isBundle
        ? FILTER_CATEGORY_CODES.BUNDLE_PRICE_FILTERS
        : FILTER_CATEGORY_CODES.PRICE_FILTERS,
      categoryName: isBundle
        ? FILTER_CATEGORY_NAMES.BUNDLE_PRICE
        : FILTER_CATEGORY_NAMES.PRICE,
      valueCode: `[${priceRangeMatch[1]} TO ${priceRangeMatch[2]}]`,
      valueName: `$${priceRangeMatch[1]} - $${priceRangeMatch[2]}`
    };
  }
}

function createProductSearchQueryFromFilters(
  filters,
  sortCode = '',
  searchText = ''
) {
  return `${searchText}:${sortCode}${(Array.isArray(filters)
    ? filters
    : []
  ).reduce((acc, filters) => {
    acc += `:${filters.categoryCode}:${filters.valueCode}`;

    return acc;
  }, '')}`;
}

function getQueryParametersFromUrl(queryParams, baseSortValue) {
  const { pathname } = window.location;
  const defaultSort = queryParams['text']
    ? 'relevance'
    : baseSortValue || 'bestSeller-asc';

  let pageNum;

  if (!queryParams['page']) {
    pageNum = 0;
  }

  try {
    pageNum = parseInt(queryParams['page']);
    if (isNaN(pageNum) || pageNum < 0) {
      pageNum = 0;
    }
  } catch (error) {
    pageNum = 0;
  }

  return {
    boltPattern: queryParams['boltPattern'],
    brand: queryParams['brand'],
    categoryCode: pathname.split('/').pop().replace('-catalog', ''),
    currentSearchOption: queryParams['currentSearchOption'],
    diameter: queryParams['diameter'],
    isSearchAll: queryParams['isSearchAll'],
    pageNum,
    query: queryParams['q'],
    searchText: queryParams['text'],
    sort: queryParams['sort'] || defaultSort,
    width: queryParams['width']
  };
}

function getFilterId(filter) {
  const { categoryCode, type, valueCode: filterValueCode } = filter || {};
  const valueCode = type !== 'slider' && filterValueCode;

  const id = [categoryCode, valueCode, type]
    .reduce((arr, val) => {
      if (typeof val === 'string') {
        arr.push(slugify(val));
      }

      return arr;
    }, [])
    .join('-');

  return id;
}

function getSeoName(categoryCode, productType) {
  let seoName = categoryCode
    ? `${categoryCode}-${productType.toLowerCase()}`
    : '';
  seoName = /\//.test(seoName) ? seoName.split('/')[1] : seoName;
  return seoName;
}

function getNewSelectedFilters(filterToToggle, selectedFilters) {
  if (!Array.isArray(selectedFilters)) {
    return [];
  }

  const matchingSelectedFilterIdx = selectedFilters.findIndex(
    selectedFilter =>
      selectedFilter.categoryCode === filterToToggle.categoryCode &&
      (filterToToggle.type === 'slider' ||
        selectedFilter.valueCode === filterToToggle.valueCode)
  );

  let newSelectedFilters;

  if (matchingSelectedFilterIdx === -1) {
    newSelectedFilters = [...selectedFilters, filterToToggle];
  } else {
    newSelectedFilters = [
      ...selectedFilters.slice(0, matchingSelectedFilterIdx),
      ...selectedFilters.slice(matchingSelectedFilterIdx + 1)
    ];

    if (filterToToggle.type === 'slider') {
      newSelectedFilters.push(filterToToggle);
    }
  }

  return newSelectedFilters;
}

function getNormalizedSortNames(sort) {
  if (/^bestAvailable/i.test(sort)) {
    return SORT_OPTIONS.BEST_AVAILABLE;
  } else if (/^bestSeller/i.test(sort)) {
    return SORT_OPTIONS.BEST_SELLER;
  } else if (/^fleet-preferences/i.test(sort)) {
    return SORT_OPTIONS.FLEET_PREFERENCES;
  } else if (/^price-asc/i.test(sort)) {
    return SORT_OPTIONS.PRICE_ASC;
  } else if (/^all/i.test(sort)) {
    return SORT_OPTIONS.HIGHEST_RATED;
  } else if (/^price-desc/i.test(sort)) {
    return SORT_OPTIONS.PRICE_DESC;
  } else if (/^rel/i.test(sort)) {
    return SORT_OPTIONS.RELEVANCE;
  } else {
    return null;
  }
}

function isChipFilter(filter) {
  return (
    filter.categoryCode !== FILTER_CATEGORY_CODES.PRICE_FILTERS &&
    filter.categoryCode !== FILTER_CATEGORY_CODES.BUNDLE_PRICE_FILTERS
  );
}

function getResultsText(
  results,
  searchText,
  productType,
  isPrebuiltPackageSearch
) {
  const isPlural = Array.isArray(results) && results.length !== 1;
  if (searchText) {
    return `results for ${searchText}`;
  }
  if (isPrebuiltPackageSearch) {
    return `package result${isPlural ? 's' : ''}`;
  }
  if (productType === PRODUCT_TYPES.TIRES) {
    return `tire result${isPlural ? 's' : ''}`;
  }
}

function getFilterCount(filters, categoryCode) {
  return (
    filters?.find(filter => filter?.categoryCode === categoryCode)?.values?.[0]
      ?.count || 0
  );
}

const __testOnly__ = {
  getEnhancedFilters,
  getFilterId,
  getMatchingSelectedFilterIndex,
  getPriceRangeFilter,
  isFleetPreferredProductFilter,
  isQuickFilter,
  sortSelectedFilters
};

export {
  createProductSearchQueryFromFilters,
  getFilterId,
  getNewSelectedFilters,
  facetsToFilters,
  facetsToSelectedFilters,
  getQueryParametersFromUrl,
  getSeoName,
  getNormalizedSortNames,
  isChipFilter,
  getResultsText,
  getFilterCount,
  __testOnly__
};
